import { Controller } from "@hotwired/stimulus";

import { reloadFrame } from "../lib/frames.js";
import { get, post } from "../lib/http.js";
import { showToast } from "../lib/toast.js";

/**
 * Поиск книги для добавления на полку.
 *
 * В макете на полке стоит кнопка «Добавить книгу»: она открывает эту модалку.
 * Строку выдачи собирает <template> из разметки — раньше её склеивал JS.
 * Добавленную книгу тоже не вклеиваем в список руками: перезагружаем
 * Turbo-фрейм полки, иначе сортировка и пагинация разъезжаются.
 */
export default class extends Controller {
  static targets = ["dialog", "input", "results", "empty", "row", "busy"];
  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    delay: { type: Number, default: 250 },
    frame: { type: String, default: "shelf-list" },
  };

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.setBusy(false);
  }

  open() {
    this.dialogTarget.showModal();
    this.inputTarget.focus();
  }

  close() {
    this.dialogTarget.close();
    this.reset();
  }

  /** Ввод: ищем после паузы в наборе. */
  search() {
    clearTimeout(this.timer);

    if (this.inputTarget.value.trim().length < this.minLengthValue) {
      this.request?.abort();
      this.reset();
      return;
    }

    this.timer = setTimeout(() => this.fetchBooks(), this.delayValue);
  }

  async fetchBooks() {
    const query = this.inputTarget.value.trim();

    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.setBusy(true);

    try {
      const data = await get(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        signal: request.signal,
      });
      this.render(data?.results ?? []);
    } catch (error) {
      // Прерванный запрос — это следующая буква в поле: спиннер оставляем
      // до ответа на последний запрос.
      if (error.name === "AbortError") return;
      showToast("Не удалось выполнить поиск. Попробуйте позже");
    } finally {
      if (this.request === request) this.setBusy(false);
    }
  }

  /** Спиннер в поле поиска. */
  setBusy(busy) {
    if (this.hasBusyTarget) this.busyTarget.hidden = !busy;
  }

  render(books) {
    this.resultsTarget.replaceChildren();
    this.emptyTarget.hidden = books.length > 0;

    for (const book of books) this.resultsTarget.append(this.row(book));
  }

  /** Строка выдачи: разметку отдаёт шаблон, здесь только значения. */
  row(book) {
    const row = this.rowTarget.content.firstElementChild.cloneNode(true);

    row.dataset.bookId = book.id;
    row.querySelector("[data-field='title']").textContent = book.name;
    row.querySelector("[data-field='author']").textContent = book.author || "";

    // Без обложки остаётся заглушка, прописанная в шаблоне.
    if (book.cover) row.querySelector("[data-field='cover']").src = book.cover;

    return row;
  }

  /** Кладёт книгу на полку со статусом «хочу прочитать». */
  async add(event) {
    const row = event.currentTarget;
    if (row.disabled) return;

    row.disabled = true;

    try {
      await post("/api/shelf/books", { book: row.dataset.bookId, status: "want" });
    } catch (error) {
      showToast(error.message);
      row.disabled = false;
      return;
    }

    showToast("Книга добавлена на полку", "success");
    row.remove();
    reloadFrame(this.frameValue);
  }

  reset() {
    this.resultsTarget.replaceChildren();
    this.emptyTarget.hidden = true;
    this.request?.abort();
    this.setBusy(false);
  }
}
